import React from 'react';
import { createPortal } from 'react-dom';
import { FileText, Tag, X } from 'lucide-react';
import { NodeType } from '../types';

interface NodeTypeModalProps {
    isOpen: boolean;
    currentType: NodeType;
    onSelect: (type: NodeType) => void;
    onClose: () => void;
    language: 'english' | 'vietnamese';
}

export const NodeTypeModal: React.FC<NodeTypeModalProps> = ({
    isOpen,
    currentType,
    onSelect,
    onClose,
    language
}) => {
    if (!isOpen) return null;

    const labels = {
        title: language === 'vietnamese' ? 'Chọn loại Node' : 'Select Node Type',
        note: {
            title: language === 'vietnamese' ? 'Note' : 'Note',
            description: language === 'vietnamese' ? 'Note đầy đủ với nội dung và hình ảnh' : 'Full note with content and images'
        },
        label: {
            title: language === 'vietnamese' ? 'Label' : 'Label',
            description: language === 'vietnamese' ? 'Nhãn tổ chức (chỉ có tiêu đề)' : 'Organizational header (title only)'
        }
    };

    const types: Array<{ value: NodeType; icon: React.ReactNode; label: string; description: string; color: string }> = [
        {
            value: 'note',
            icon: <FileText size={24} />,
            label: labels.note.title,
            description: labels.note.description,
            color: 'blue'
        },
        {
            value: 'label',
            icon: <Tag size={24} />,
            label: labels.label.title,
            description: labels.label.description,
            color: 'violet'
        }
    ];

    return createPortal(
        <>
            {/* Modal Wrapper - Handles backdrop click */}
            <div
                className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50 animate-in fade-in"
                style={{ pointerEvents: 'auto' }}
                onClick={onClose}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div
                    className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-200 cursor-default"
                    onClick={(e) => e.stopPropagation()}
                >
                    {/* Header */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                            {labels.title}
                        </h2>
                        <button
                            onClick={onClose}
                            className="p-1 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                            <X size={20} className="text-gray-500" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-3">
                        {types.map(type => (
                            <button
                                key={type.value}
                                onClick={() => {
                                    onSelect(type.value);
                                    onClose();
                                }}
                                className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-left ${currentType === type.value
                                    ? `border-${type.color}-500 bg-${type.color}-50 dark:bg-${type.color}-900/20`
                                    : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-700/50'
                                    }`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className={`p-2 rounded-lg ${currentType === type.value
                                        ? `bg-${type.color}-100 dark:bg-${type.color}-900/40 text-${type.color}-600 dark:text-${type.color}-400`
                                        : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-400'
                                        }`}>
                                        {type.icon}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-semibold text-gray-900 dark:text-white">
                                                {type.label}
                                            </h3>
                                            {currentType === type.value && (
                                                <span className={`text-xs px-2 py-0.5 rounded-full bg-${type.color}-100 dark:bg-${type.color}-900/40 text-${type.color}-700 dark:text-${type.color}-400 font-medium`}>
                                                    {language === 'vietnamese' ? 'Hiện tại' : 'Current'}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                            {type.description}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </>,
        document.body
    );
};
